
























import {
  defineComponent,
  computed,
  useContext,
  useRoute
} from '@nuxtjs/composition-api';
import {
  getSSRRequestFullUrl,
  getMetaTags,
  mediaUrlFallback
} from '~/helpers/utils';
import { useHeader } from '@vue-storefront/novulo';
import useUrlFormatter from '~/hooks/useUrlFormatter';
import useGoogleAnalytics from '~/hooks/useGoogleAnalytics';
import { onSSR } from '@vue-storefront/core';
import { CacheTagPrefix } from '@vue-storefront/cache';
import useBreadcrumbsStructuredData from '~/hooks/useBreadcrumbsStructuredData';
import useCurrentPage from '~/hooks/useCurrentPage';
import useStructuredData from '~/hooks/useStructuredData';

export const DESKTOP_BREAKPOINT = 768;

export default defineComponent({
  name: 'CmsPage',
  props: {
    urlInfo: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const { i18n, res, $config } = useContext();
    const { isHomePage, isPage } = useCurrentPage();

    const formatUrl = useUrlFormatter();
    const route = useRoute();
    useGoogleAnalytics();

    const { headerResult } = useHeader();

    const uspsGetter = computed(() => {
      if (!Array.isArray(headerResult.value)) return [];
      return headerResult.value.find((d) => d.code === 'header_usps')
        ?.elements?.[0]?.urls;
    });

    const isClp = computed(() => {
      return isPage('clp');
    });

    // deep copy props.urlInfo.containers to avoid vuex mutation error
    const containers = computed(() => {
      return JSON.parse(JSON.stringify(props.urlInfo?.containers || []));
    });

    onSSR(() => {
      const productTags = containers.value
        .filter((container) => container.code === 'productcarousel_container')
        .reduce((acc, container) => {
          const carouselElement = container.elements.find(
            (element) => element.code === 'productcarousel'
          );
          if (!carouselElement) return acc;
          const products = carouselElement?.products || [];
          // product_tags is an array of objects with a products array NOT to be confused with tags
          const product_tags =
            carouselElement?.product_tags.flatMap((tag) => tag.products) || [];
          return [
            ...acc,
            ...products.map(
              (product) => `${CacheTagPrefix.Product}${product.product_id}`
            ),
            ...product_tags.map(
              (product) => `${CacheTagPrefix.Product}${product.product_id}`
            )
          ];
        }, []);

      res.setHeader(
        'Cache-Tag',
        `CMS,${CacheTagPrefix.View}content,${CacheTagPrefix.View}${
          route.value.fullPath
        }${isHomePage.value ? ',homepage' : ''}${
          productTags?.length ? `,${productTags.join(',')}` : ''
        }`
      );
      res.setHeader(
        'Cache-Control',
        `max-age=${$config.cfBrowserCacheTimeout}, public`
      );
      res.setHeader(
        'Cloudflare-CDN-Cache-Control',
        `max-age=${$config.cfCacheTimeout}, public`
      );
    });

    const slug = route.value.params.slug;
    const pageName = slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : '';
    const pageTitle = props.urlInfo?.meta_title || pageName;
    const fallbackBreadcrumbTitle = computed(() => {
      const redirects = props.urlInfo?.cmswebsiteredirects_headless_pages || [];
      const currentLanguage = i18n.locale?.toLowerCase?.() || '';
      const currentLanguageCode = currentLanguage.split('-')[0];
      const currentLocale = currentLanguage.includes('-')
        ? currentLanguage
        : [currentLanguage, $config?.domainConfig?.websiteCountry?.toLowerCase?.()]
          .filter(Boolean)
          .join('-');

      const matchingRedirect =
        redirects.find(
          ({ locale }) => locale?.toLowerCase?.() === currentLocale
        ) ??
        redirects.find(({ locale }) =>
          locale?.toLowerCase?.().startsWith(`${currentLanguageCode}-`)
        );

      return matchingRedirect?.breadcrumb || props.urlInfo?.page_title || '';
    });
    const breadcrumbs = computed(() => {
      if (isHomePage.value) return [];

      const apiBreadcrumbsContainer =
        containers.value.find((c) => c.code === 'breadcrumb')?.elements?.[0] ||
        null;
      const apiBreadcrumbs =
        apiBreadcrumbsContainer?.urls
          ?.map((url) => ({
            link: url.url,
            text: url.title
          }))
          .filter((breadcrumb) => breadcrumb.text) || [];
      const currentPageTitle =
        apiBreadcrumbsContainer?.page_title || fallbackBreadcrumbTitle.value;
      const crumbs = [
        {
          link: formatUrl('/'),
          text: i18n.t('breadcrumbs_home')
        },
        ...apiBreadcrumbs,
        currentPageTitle
          ? {
            link: '#',
            text: currentPageTitle
          }
          : null
      ].filter((crumb) => crumb?.text);

      return crumbs.length > 1 ? crumbs : [];
    });

    const { structuredData } = useStructuredData(containers);

    const showUspsBar = computed(() => {
      return uspsGetter.value ? uspsGetter.value.length : false;
    });

    return {
      breadcrumbs,
      containers,
      i18n,
      isClp,
      isHomePage,
      pageTitle,
      showUspsBar,
      structuredData,
      uspsGetter
    };
  },
  head() {
    const pageURL = process.server
      ? getSSRRequestFullUrl(this.$ssrContext.req)
      : window.location.href;

    const canonicalUrl = pageURL.split('?')[0];

    // hreflang tags have to be set manually because the relative url changes per locale
    const hrefLangTags =
      this.urlInfo.cmswebsiteredirects_headless_pages?.map((redirect) => ({
        hid: `hreflang-${redirect.locale}`,
        rel: 'alternate',
        hreflang: redirect.locale,
        href: redirect.url
      })) || [];

    const slug = this.$route.params.slug;
    const pageName = slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : '';
    const pageTitle = this.urlInfo?.meta_title || pageName;

    const customBreadcrumbs = this.isHomePage
      ? [{ link: '/', text: this.$i18n.t('breadcrumbs_home') }]
      : this.breadcrumbs;

    const breadcrumbsStructuredData = useBreadcrumbsStructuredData(
      customBreadcrumbs,
      this.$ssrContext
    );

    return {
      title: pageTitle,
      meta: getMetaTags({
        charset: 'utf-8',
        title: pageTitle,
        description: this.urlInfo?.meta_description || '',
        image: mediaUrlFallback(
          '/media/4e067df4-a171-4c3a-8cfb-63cb87a0cbbd/logo%20kamera%20express.svg'
        ),
        url: pageURL,
        locale: this.$i18n.locale,
        type: this.urlInfo?.page_type || ''
      }),
      link: [
        {
          rel: 'canonical',
          href: canonicalUrl
        },
        ...hrefLangTags
      ],
      __dangerouslyDisableSanitizers: ['script'],
      script: [
        {
          hid: 'org-ldjson',
          type: 'application/ld+json',
          json: this.structuredData
        },
        {
          hid: 'breadcrumbs-ldjson',
          type: 'application/ld+json',
          json: breadcrumbsStructuredData
        }
      ]
    };
  }
});
